export interface Task {
  id: string
  userId: string
  title: string
  description?: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  dueDate?: string // ISO date string
  estimatedMinutes?: number
  actualMinutes?: number
  category: 'shoot_prep' | 'editing' | 'client_communication' | 'admin' | 'marketing' | 'other'
  tags?: string[]
  
  // Weekly tracking
  weekStartDate: string // ISO date string for the Monday of the week this task belongs to
  weekNumber: number // Week number of the year
  year: number
  
  // AI automation (premium feature)
  isAiGenerated?: boolean
  aiSuggestions?: string[]
  autoScheduled?: boolean
  
  // Related entities
  clientId?: string
  shootId?: string
  invoiceId?: string
  
  // Timestamps
  createdAt: string
  updatedAt: string
  completedAt?: string
  
  // Related data (populated when needed)
  client?: {
    id: string
    name: string
    company?: string
  }
  shoot?: {
    id: string
    title: string
    date: string
  }
}

export interface WeeklyTaskLimits {
  weekStartDate: string
  weekNumber: number
  year: number
  currentWeekUsage: number
  maxTasksPerWeek: number
  canCreateTask: boolean
  isUnlimited: boolean // true for premium users
}

export interface TaskStats {
  totalTasks: number
  completedTasks: number
  pendingTasks: number
  inProgressTasks: number
  overdueTasks: number
  thisWeekTasks: number
  thisWeekCompleted: number
  averageCompletionTime: number // in minutes
  productivityScore: number // 0-100 based on completion rate and timeliness
}

export interface TaskFilters {
  status?: Task['status'][]
  priority?: Task['priority'][]
  category?: Task['category'][]
  clientId?: string
  shootId?: string
  dueDate?: {
    from?: string
    to?: string
  }
  search?: string
  weekStartDate?: string // Filter by specific week
}

export interface CreateTaskData {
  title: string
  description?: string
  priority: Task['priority']
  category: Task['category']
  dueDate?: string
  estimatedMinutes?: number
  tags?: string[]
  clientId?: string
  shootId?: string
  invoiceId?: string
}

export interface UpdateTaskData {
  title?: string
  description?: string
  priority?: Task['priority']
  status?: Task['status']
  category?: Task['category']
  dueDate?: string
  estimatedMinutes?: number
  actualMinutes?: number
  tags?: string[]
  clientId?: string
  shootId?: string
  invoiceId?: string
}

export interface AITaskSuggestion {
  id: string
  title: string
  description: string
  priority: Task['priority']
  category: Task['category']
  estimatedMinutes: number
  reasoning: string // AI explanation for why this task is suggested
  confidence: number // 0-100 confidence score
  basedOn: 'shoot' | 'client' | 'pattern' | 'deadline' | 'learning' | 'workflow' // What triggered this suggestion
  relatedEntityId?: string
  isLearningNotice?: boolean // Special flag for learning mode notices
}

export interface TaskTemplate {
  id: string
  name: string
  description: string
  tasks: {
    title: string
    description: string
    priority: Task['priority']
    category: Task['category']
    estimatedMinutes: number
    daysFromTrigger: number // How many days before/after the trigger event
  }[]
  triggerType: 'shoot' | 'client_onboarding' | 'invoice_sent' | 'manual'
  isAiGenerated: boolean
  userId: string
  createdAt: string
}

// Weekly task management utilities
export interface WeekInfo {
  weekStartDate: string // Monday of the week
  weekEndDate: string // Sunday of the week
  weekNumber: number
  year: number
  isCurrentWeek: boolean
}

export const getWeekInfo = (date: Date = new Date()): WeekInfo => {
  const currentDate = new Date(date)
  
  // Get Monday of the week (Monday = start of week)
  const dayOfWeek = currentDate.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  let daysToMonday: number
  
  if (dayOfWeek === 0) {
    // If it's Sunday, it belongs to the week starting the NEXT Monday
    daysToMonday = 1
  } else {
    // For Monday through Saturday, go back to the Monday of the current week
    daysToMonday = 1 - dayOfWeek
  }
  
  const monday = new Date(currentDate)
  monday.setDate(currentDate.getDate() + daysToMonday)
  monday.setHours(0, 0, 0, 0)
  
  // Get Sunday of the week
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  sunday.setHours(23, 59, 59, 999)
  
  // Calculate week number
  const startOfYear = new Date(monday.getFullYear(), 0, 1)
  const weekNumber = Math.ceil(((monday.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7)
  
  // Check if it's current week
  const now = new Date()
  const isCurrentWeek = monday <= now && now <= sunday
  
  return {
    weekStartDate: monday.toISOString().split('T')[0],
    weekEndDate: sunday.toISOString().split('T')[0],
    weekNumber,
    year: monday.getFullYear(),
    isCurrentWeek
  }
}

export const formatWeekRange = (weekStartDate: string): string => {
  const start = new Date(weekStartDate)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  
  const formatOptions: Intl.DateTimeFormatOptions = { 
    month: 'short', 
    day: 'numeric' 
  }
  
  if (start.getMonth() === end.getMonth()) {
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.getDate()}`
  } else {
    return `${start.toLocaleDateString('en-US', formatOptions)} - ${end.toLocaleDateString('en-US', formatOptions)}`
  }
} 